import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TOOLTIPS = {
  email: "If enabled, an email will be generated and sent to the Franchise General Inbox for every new project.",
  paidCommission: "Commissions equal to or less than the Paid Commission value will automatically move to the 'PAID Commissions' page. These projects will no longer appear on the 'Commissions & Bonuses' page.",
  alertValue: "Commissions equal to or less than the Commission Alert value will automatically move to the 'Commissions Alerts' page. This alert is typically triggered because an expense was entered after the project status was marked as 'Closed,' and negative numbers require action. The alert will prompt the necessary action to address the issue.",
  noteTracking: "Projects Note Tracking Max Days sets how far back the report looks for note updates. Only projects with a last note update in the past X or more calendar days within the last Y days (this value) will be included in the report. This helps identify projects with outdated notes during a specific timeframe.",
};

const HelpTip = ({ id, text }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const title = { email: 'New Project Email', paidCommission: 'Paid Commission Value', alertValue: 'Commissions Alert Value', noteTracking: 'Projects Note Tracking Max Days' }[id];

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen(!open)}
        className="text-[#5B8DB8] hover:text-[#4a7ca5] ml-2"
        data-testid={`help-${id}`}
      >
        <HelpCircle className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute left-8 top-[-10px] z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4" data-testid={`tooltip-${id}`}>
          <h4 className="font-bold text-gray-800 mb-2 text-sm">{title}</h4>
          <p className="text-gray-600 text-sm leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const [newProjectEmail, setNewProjectEmail] = useState(true);
  const [paidCommissionValue, setPaidCommissionValue] = useState('.10');
  const [commissionsAlertValue, setCommissionsAlertValue] = useState('-5.00');
  const [noteTrackingDays, setNoteTrackingDays] = useState('181');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/settings`)
      .then(readResponseJSON)
      .then(data => {
        setNewProjectEmail(data.new_project_email_enabled ?? true);
        setPaidCommissionValue(String(data.paid_commission_value ?? '.10'));
        setCommissionsAlertValue(String(data.commissions_alert_value ?? '-5.00'));
        setNoteTrackingDays(String(data.projects_note_tracking_max_days ?? '181'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      const res = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_project_email_enabled: newProjectEmail,
          paid_commission_value: parseFloat(paidCommissionValue) || 0,
          commissions_alert_value: parseFloat(commissionsAlertValue) || 0,
          projects_note_tracking_max_days: parseInt(noteTrackingDays) || 0,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Settings saved successfully');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  if (loading) return <Layout><div className="p-8 text-center text-gray-500">Loading settings...</div></Layout>;

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic" data-testid="settings-title">Settings</h1>
        </div>

        <div className="bg-white rounded shadow p-8 max-w-3xl">
          <div className="space-y-6">
            {/* New Project Email */}
            <div className="flex items-center">
              <label className="text-gray-800 text-sm w-64 text-right pr-6">New Project Email</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newProjectEmail} onChange={(e) => setNewProjectEmail(e.target.checked)} className="w-4 h-4 text-blue-600 rounded" data-testid="email-toggle" />
                <span className="text-sm text-gray-600">Enabled</span>
              </label>
              <HelpTip id="email" text={TOOLTIPS.email} />
            </div>

            {/* Paid Commission Value */}
            <div className="flex items-center">
              <label className="text-gray-800 text-sm w-64 text-right pr-6">Paid Commission Value</label>
              <div className="flex items-center gap-1">
                <span className="text-gray-500 text-sm">$</span>
                <input type="text" value={paidCommissionValue} onChange={(e) => setPaidCommissionValue(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-36 text-sm" data-testid="paid-commission-input" />
              </div>
              <HelpTip id="paidCommission" text={TOOLTIPS.paidCommission} />
            </div>

            {/* Commissions Alert Value */}
            <div className="flex items-center">
              <label className="text-gray-800 text-sm w-64 text-right pr-6">Commissions Alert Value</label>
              <div className="flex items-center gap-1">
                <span className="text-gray-500 text-sm">$</span>
                <input type="text" value={commissionsAlertValue} onChange={(e) => setCommissionsAlertValue(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-36 text-sm" data-testid="alert-value-input" />
              </div>
              <HelpTip id="alertValue" text={TOOLTIPS.alertValue} />
            </div>

            {/* Projects Note Tracking Max Days */}
            <div className="flex items-center">
              <label className="text-gray-800 text-sm w-64 text-right pr-6">Projects Note Tracking Max Days</label>
              <div className="flex items-center gap-1">
                <input type="text" value={noteTrackingDays} onChange={(e) => setNoteTrackingDays(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-36 text-sm" data-testid="note-tracking-input" />
                <span className="text-gray-500 text-sm">days</span>
              </div>
              <HelpTip id="noteTracking" text={TOOLTIPS.noteTracking} />
            </div>

            {/* Save Button */}
            <div className="flex justify-center pt-4">
              <button onClick={handleSave} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-8 py-2.5 rounded text-sm font-medium transition-colors" data-testid="save-settings-btn">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
